import React, { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useHistory } from "react-router-dom";

import {
  AnalyticsEvents,
  CommunityLevelSection,
  EolasFile,
  NationalResourcesClickPayload,
  NationalResourceEntityType,
} from "@eolas-medical/core";

import { NavButton, PageTitle, InnerPageWrapper } from "UIKit";

import { snakeCase } from "lodash";
import useNationalBodies from "modules/nationalResources/data/useNationalBodies";
import { trackEvent } from "API/Analytics";
import { useMainSectionVisit, useSelectFile } from "Hooks";
import { EolasList } from "Components/EolasFileList/EolasList";
import useSearchNationalResources from "modules/nationalResources/data/useSearchNationalResources";
import KnowledgeSectionItem from "Pages/Knowledge/components/KnowledgeSectionItem/KnowledgeSectionItem";
import { CommunitySectionType } from "../ClinicalCalculators/types";
import { BaseAppRoute } from "Utilities/types";

const NationalGuidelines: React.FC<RouteComponentProps> = observer(({ match: { url } }) => {
  const [searchInput, setSearchInput] = useState("");
  const [searchResults, setSearchResults] = useState<any>([]);
  const [showSearchResult, setShowSearchResult] = useState(false);

  const history = useHistory();
  const { onSelectFile } = useSelectFile();

  useMainSectionVisit({
    mainSectionId: CommunityLevelSection.nationalGuidelines,
  });

  const { nationalBodies, gettingNationalBodies } = useNationalBodies(
    NationalResourceEntityType.GUIDELINE,
  );

  const { searchNationalResources, searchingNationalResources } = useSearchNationalResources();

  const { t } = useTranslation();

  const handleClickBody = useCallback(
    ({ name }) => {
      const snakeCaseBody = snakeCase(name);
      history.push({
        pathname: `/${BaseAppRoute.knowledge}/nationalGuidelines/${snakeCaseBody}`,
        state: { body: name },
      });
    },
    [history],
  );

  const handleClickGuideline = (guideline: EolasFile) => {
    trackEvent(AnalyticsEvents.NATIONAL_RESOURCE_SELECTED, {
      nationalResourceType: NationalResourceEntityType.GUIDELINE,
      resourceName: guideline.name,
    } as NationalResourcesClickPayload);
    onSelectFile(guideline);
  };

  const handleSearchGuidelines = useCallback(() => {
    setShowSearchResult(true);
    searchNationalResources(
      {
        entityType: NationalResourceEntityType.GUIDELINE,
        query: searchInput,
        mode: "searchbyInput",
      },
      {
        onSuccess: (response) => {
          setSearchResults(response);
        },
      },
    );
  }, [searchInput, searchNationalResources]);

  const handleClearSearch = useCallback(() => {
    setSearchInput("");
    setShowSearchResult(false);
  }, []);

  useEffect(() => {
    if (searchInput === "") {
      setShowSearchResult(false);
    }
  }, [searchInput]);

  return (
    <>
      <InnerPageWrapper>
        <PageTitle title={t("community_nationalGuidelines_title")} />
        <NavButton onClick={() => history.goBack()} />
        <EolasList<EolasFile, "click">
          value={searchInput}
          placeholderSearchText={t("findNationalGuidelines_searchPlaceholder")}
          items={showSearchResult ? searchResults : nationalBodies}
          isSearchable
          isLoading={searchingNationalResources || gettingNationalBodies}
          searchType="click"
          onSearchInputChange={setSearchInput}
          onClearSearch={handleClearSearch}
          onClickSearch={handleSearchGuidelines}
          renderItem={(eolasFile) => (
            <KnowledgeSectionItem
              eolasFile={eolasFile}
              onSelectFile={eolasFile.description ? handleClickGuideline : handleClickBody}
              sectionType={
                eolasFile.description
                  ? CommunitySectionType.NATIONAL_RESOURCE
                  : CommunitySectionType.BODY
              }
            />
          )}
        />
      </InnerPageWrapper>
    </>
  );
});

export default NationalGuidelines;
